/**
 * A BALANÇA — mexer nos pratos até sobrar um saco sozinho.
 *
 * A figura é redesenhada a cada ação, como a prancheta de construção faz. Por cima dela
 * vai uma camada de alvos invisíveis, um por peça: clicar numa peça tira aquela peça
 * daquele prato. **Não há barra de ferramentas para "tirar peso":** a peça é o botão, o
 * que torna o gesto direto e dispensa explicar qual ferramenta faz o quê.
 *
 * A única ação que não é uma peça é **repartir**, que vale para os dois pratos ao mesmo
 * tempo — repartir um prato só não é engano que alguém cometa, é outra conta. Ela fica
 * num botão, e só acende quando cabe: quando há sacos sozinhos de um lado e o número
 * deles divide tudo o que está do outro.
 *
 * Teclado: Tab chega a cada peça, Espaço ou Enter tira. Desfazer é um botão e `Ctrl+Z`.
 */

import { aplicarAcoes, ladoQuePende, repartirPossivel, sacoSozinho } from '../engine/grade.ts';
import { grupoDaCena } from './coords.ts';
import { capitalizar, conta, oS, umA, verbo } from '../core/pt.ts';
import { balanca, describeBalanca, type PratoDesenho } from '../render/balanca.ts';
import { renderScene } from '../render/svg.ts';
import type { AcaoBalanca, BalanceOpsInteraction, EstadoBalanca } from '../schema/item.ts';

const NS = 'http://www.w3.org/2000/svg';

export interface Balanca {
  readonly element: HTMLElement;
  acoes(): readonly AcaoBalanca[];
  estado(): EstadoBalanca;
  /** O aluno chegou a mexer? Sem isso, não há resposta. */
  tocado(): boolean;
  travar(): void;
}

type Publico = Pick<
  BalanceOpsInteraction,
  'descricao' | 'inicial' | 'recipiente' | 'incognita' | 'unidade'
>;

/**
 * @param feitas As ações já feitas — na revisão, as do aluno: a balança mostra a resposta
 *   entregue, e não a do começo.
 */
export function criarBalanca(
  inter: Publico,
  bloqueadoDesdeInicio: boolean,
  feitas: readonly AcaoBalanca[] = [],
): Balanca {
  const acoes: AcaoBalanca[] = [...feitas];
  let bloqueado = bloqueadoDesdeInicio;

  const root = document.createElement('div');
  root.className = `balanca${bloqueado ? ' is-bloqueado' : ''}`;

  const palco = document.createElement('div');
  palco.className = 'balanca-palco';

  const barra = document.createElement('div');
  barra.className = 'balanca-barra';

  const btnRepartir = document.createElement('button');
  btnRepartir.type = 'button';
  btnRepartir.className = 'app-btn-mini balanca-repartir';

  const btnDesfazer = document.createElement('button');
  btnDesfazer.type = 'button';
  btnDesfazer.className = 'app-btn-mini';
  btnDesfazer.textContent = 'Desfazer';

  const contador = document.createElement('span');
  contador.className = 'balanca-conta';

  barra.append(btnRepartir, btnDesfazer, contador);

  const dica = document.createElement('p');
  dica.className = 'balanca-dica';

  root.append(palco, barra, dica);

  const estadoAgora = (): EstadoBalanca => aplicarAcoes(inter.inicial, acoes);

  /**
   * Para que lado pende: pelo PESO de cada prato, como numa balança de verdade (ver
   * `ladoQuePende`). Contava peças tiradas sem distinguir o recipiente do peso de 1 kg,
   * e tirar um pote de um prato e um peso do outro deixava o braço reto.
   */
  const pende = (): 'esq' | 'dir' | 'nenhum' => ladoQuePende(inter.inicial, estadoAgora());

  /** Repartir só com o braço reto — a mesma regra que o corretor aplica (`repartirPossivel`). */
  const partesAgora = (): number | null => repartirPossivel(inter.inicial, estadoAgora());

  function desenhar(): void {
    const e = estadoAgora();
    const lado = pende();
    const desc = describeBalanca(
      e.esq,
      e.dir,
      inter.recipiente,
      inter.incognita,
      inter.unidade,
      lado,
    );
    const svg = renderScene(
      balanca({
        esq: e.esq,
        dir: e.dir,
        incognita: inter.incognita,
        pende: lado,
        title: 'Balança de dois pratos',
        desc,
      }),
    );
    camadaDeAlvos(svg, e);
    palco.replaceChildren(svg);

    const k = partesAgora();
    btnRepartir.textContent =
      k === null ? 'Repartir em partes iguais' : `Repartir os dois pratos em ${k}`;
    btnRepartir.disabled = bloqueado || k === null;
    btnRepartir.title =
      lado === 'nenhum' ? '' : 'Com a balança torta, repartir não mantém os pratos iguais.';
    btnDesfazer.disabled = bloqueado || acoes.length === 0;
    contador.textContent = String(conta(acoes.length, 'movimento'));

    const pronto = sacoSozinho(e) !== null;
    dica.textContent = bloqueado
      ? 'Resposta enviada.'
      : pronto
        ? `${capitalizar(umA(inter.recipiente).adj('sozinho'))} de um lado e pesos do outro: ${oS(inter.recipiente)} ${verbo('pesar.pres', 'sg')} o que os pesos mostram.`
        : lado === 'nenhum'
          ? 'Clique numa peça para tirá-la daquele prato.'
          : 'Os pratos deixaram de valer o mesmo. Tire a mesma coisa do outro prato.';
  }

  /** Um alvo invisível sobre cada peça; a peça é o botão. */
  function camadaDeAlvos(svg: SVGSVGElement, e: EstadoBalanca): void {
    const camada = document.createElementNS(NS, 'g');
    camada.setAttribute('class', 'balanca-alvos');
    // Copiamos as coordenadas dos retângulos que a cena já desenhou, em vez de refazer
    // a geometria da figura aqui — e por isso a camada precisa entrar NO MESMO GRUPO
    // que eles. A cena inteira mora dentro de um `<g>` que inverte o eixo Y (no modelo
    // y cresce para cima, no SVG para baixo, ver `render/svg.ts`); pendurada na raiz do
    // SVG, a camada não recebia essa inversão e todo alvo saía espelhado na vertical —
    // o clique caía longe da peça, com a balança reta ou torta.
    const pecas = [...svg.querySelectorAll('rect')].filter((r) => !r.classList.contains('s-muted'));
    let i = 0;
    for (const prato of ['esq', 'dir'] as const) {
      const p: PratoDesenho = e[prato];
      for (let j = 0; j < p.sacos + p.pesos; j++) {
        const alvo = pecas[i];
        i++;
        if (!alvo) continue;
        const saco = j < p.sacos;
        const r = document.createElementNS(NS, 'rect');
        r.setAttribute('class', 'balanca-peca');
        for (const at of ['x', 'y', 'width', 'height'])
          r.setAttribute(at, alvo.getAttribute(at) ?? '0');
        r.setAttribute('role', 'button');
        r.setAttribute('tabindex', bloqueado ? '-1' : '0');
        r.setAttribute(
          'aria-label',
          `Tirar ${saco ? umA(inter.recipiente) : `um peso de 1 ${inter.unidade}`} do prato da ${prato === 'esq' ? 'esquerda' : 'direita'}`,
        );
        const tirar = (): void => {
          if (bloqueado) return;
          acoes.push({ t: saco ? 'tirar-saco' : 'tirar-peso', prato });
          desenhar();
        };
        r.addEventListener('click', tirar);
        r.addEventListener('keydown', (ev) => {
          if (ev.key !== ' ' && ev.key !== 'Enter') return;
          ev.preventDefault();
          tirar();
        });
        camada.appendChild(r);
      }
    }
    // A camada entra no MESMO grupo das peças, que é quem carrega a inversão do eixo
    // Y: aqui não se desenha texto, então não há rótulo para sair de cabeça para
    // baixo, e não converter nada é uma conversão a menos para esquecer. Foi o que
    // aconteceu — ver `ui/coords.ts`.
    grupoDaCena(svg, pecas[0]).appendChild(camada);
  }

  btnRepartir.addEventListener('click', () => {
    if (bloqueado) return;
    const k = partesAgora();
    if (k === null) return;
    acoes.push({ t: 'repartir', partes: k });
    desenhar();
  });
  btnDesfazer.addEventListener('click', () => {
    if (bloqueado || acoes.length === 0) return;
    acoes.pop();
    desenhar();
  });
  root.addEventListener('keydown', (e) => {
    if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
    e.preventDefault();
    if (bloqueado || acoes.length === 0) return;
    acoes.pop();
    desenhar();
  });

  desenhar();

  return {
    element: root,
    acoes: () => [...acoes],
    estado: estadoAgora,
    tocado: () => acoes.length > 0,
    travar() {
      bloqueado = true;
      root.classList.add('is-bloqueado');
      desenhar();
    },
  };
}
